import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MapPin, X, Loader2 } from "lucide-react";

/**
 * LocationAutocomplete — search any Italian place via /api/geocode/search.
 * Props:
 *  - value: { name, label, lat, lng } | null
 *  - onChange(loc | null)
 *  - placeholder
 *  - onlySettlements (default true) — true for cities/towns, false for waypoints/landmarks
 *  - testId
 */
export default function LocationAutocomplete({
  value,
  onChange,
  placeholder = "Cerca una località...",
  onlySettlements = true,
  testId = "location-autocomplete",
  required = false,
}) {
  const [query, setQuery] = useState(value?.label || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (value?.label && value.label !== query) {
      setQuery(value.label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.label]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const search = async (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/geocode/search", {
        params: { q: q.trim(), limit: 8, only_settlements: onlySettlements },
      });
      setResults(data);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const onInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    setHighlight(-1);
    // Only clear the chosen value if the user really edited the displayed label.
    // Compare against BOTH label and name so legacy rides (no label) don't get
    // wiped + lose meeting_notes when the user briefly focuses the field.
    const currentDisplay = value ? (value.label || value.name || "") : "";
    if (value && v !== currentDisplay) onChange(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 280);
  };

  const pick = (item) => {
    onChange({
      name: item.name,
      label: item.label,
      lat: item.lat,
      lng: item.lng,
      // preserve meeting_notes if user typed it before changing the place
      meeting_notes: value?.meeting_notes || "",
    });
    setQuery(item.label);
    setOpen(false);
  };

  const clear = () => {
    onChange(null);
    setQuery("");
    setResults([]);
  };

  const onKey = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); pick(results[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
        <input
          data-testid={testId}
          type="text"
          required={required}
          value={query}
          onChange={onInput}
          onFocus={() => { setOpen(true); if (results.length === 0 && query.length >= 2) search(query); }}
          onKeyDown={onKey}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm pl-10 pr-10 py-3 text-sm text-gray-900"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 animate-spin" />}
        {!loading && query && (
          <button type="button" onClick={clear} data-testid={`${testId}-clear`} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-900 p-1">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {open && (results.length > 0 || (query.length >= 2 && !loading)) && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-sm shadow-xl max-h-80 overflow-y-auto">
          {results.length === 0 ? (
            <div className="px-4 py-3 text-xs text-gray-500">Nessun risultato per "{query}"</div>
          ) : (
            results.map((r, i) => (
              <button
                type="button"
                key={r.id}
                data-testid={`${testId}-opt-${i}`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pick(r)}
                className={`w-full text-left px-4 py-2.5 border-b border-gray-100 last:border-b-0 transition-colors ${
                  i === highlight ? "bg-gray-100" : "bg-transparent"
                }`}
              >
                <div className="text-sm text-gray-900 font-medium leading-tight">{r.name}</div>
                {r.label !== r.name && (
                  <div className="text-[11px] text-gray-500 mt-0.5 truncate">{r.label}</div>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
